import asyncRetry from "async-retry";
import { ReploError } from "schemas/errors";

class CloudflareUploadError extends ReploError {}
export class UploadCanceledError extends ReploError {}

interface SignedUrlResponse {
  signedUrl: string;
  publicUrl: string;
}

interface CloudflareUploadParams {
  file: Blob;
  filename: string;
  projectId: string;
  fileId?: string;
  fetchSignedUrl: (params: {
    projectId: string;
    filename: string;
    fileId?: string;
    contentType: string;
    contentLength: number;
  }) => Promise<SignedUrlResponse>;
  errorContext?: string;
}

const isServerError = (status: number) => status >= 500;

export const uploadToCloudflare = async ({
  file,
  filename: originalFilename,
  projectId,
  fileId,
  fetchSignedUrl,
  errorContext = "file",
  onProgress,
  setCancelHandler,
}: CloudflareUploadParams & {
  onProgress?: (loadedBytes: number, totalBytes: number) => void;
  setCancelHandler?: (cancel: () => void) => void;
}): Promise<string> => {
  const { signedUrl, publicUrl } = await fetchSignedUrl({
    projectId,
    filename: originalFilename,
    fileId,
    contentType: file.type,
    contentLength: file.size,
  });

  await asyncRetry(
    async (bail) => {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signedUrl, true);
        xhr.setRequestHeader("Content-Type", file.type);
        setCancelHandler?.(() => xhr.abort());
        if (onProgress) {
          xhr.upload.addEventListener("progress", (event: ProgressEvent) => {
            if (event.lengthComputable) {
              onProgress(event.loaded, event.total);
            } else {
              onProgress(event.loaded, file.size);
            }
          });
        }
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            const error = new CloudflareUploadError({
              message: `Failed to upload ${errorContext} ${originalFilename} to Cloudflare`,
              additionalData: { status: xhr.status },
            });

            // NOTE (Noah, 2026-02-16, REPL-21481): Only retry on 5xx errors
            // from Cloudflare R2. 4xx errors (auth, bad request) won't
            // resolve on retry.
            if (!isServerError(xhr.status)) {
              bail(error);
              return;
            }

            reject(error);
          }
        });
        xhr.addEventListener("abort", () => {
          bail(
            new UploadCanceledError({
              message: `Upload of ${errorContext} ${originalFilename} was canceled`,
              additionalData: {
                filename: originalFilename,
                projectId,
                errorContext,
              },
            }),
          );
        });
        xhr.addEventListener("error", () => {
          reject(
            new CloudflareUploadError({
              message: `Network error uploading ${errorContext} ${originalFilename}`,
            }),
          );
        });
        xhr.send(file);
      });
    },
    { retries: 3, minTimeout: 500 },
  );

  return publicUrl;
};
